/**
 * The wording and rules of email verification, kept pure so they can be tested on their own.
 *
 * A new sign-up cannot sign in until it confirms the link emailed to it. Every other account - those
 * that existed before verification, and Sub-Admins a Manager creates - signs in unverified and is
 * shown a banner until it verifies. Until then no Manager's broadcast reaches a Player, so a Player's
 * email switches are greyed out with the same sentence. The Web Admin gets no banner.
 */

export const PLAYER_VERIFY_MESSAGE =
  "Please, verify your email. Until then, you won't receive emails from your Managers.";
export const STAFF_VERIFY_MESSAGE = "Please, verify your email.";

export const VERIFY_EMAIL_BUTTON = "Verify email";
export const RESEND_VERIFICATION_BUTTON = "Resend verification email";

/**
 * The banner's sentence for a role: the Player's says what they miss, a Manager's or a Sub-Admin's
 * only asks.
 *
 * @param {string|null|undefined} role The stored role name.
 * @returns {string|null} null for a role that gets no banner (the Web Admin) or none at all.
 */
export const bannerMessage = (role) => {
  if (role === "Player") return PLAYER_VERIFY_MESSAGE;
  if (role === "Manager" || role === "Admin") return STAFF_VERIFY_MESSAGE;
  return null;
};

/**
 * What a sign-up page says once Player/Add or Manager/Add has accepted the account.
 *
 * @param {{verificationRequired?: boolean, verificationEmailSent?: boolean}} result The server's answer.
 * @param {string} email The address the account was created with.
 * @returns {string}
 */
export const signUpMessage = (result, email) => {
  if (!result?.verificationRequired) return "Your account has been created. Please log in to continue.";
  if (result.verificationEmailSent) {
    return `Your account has been created. We've sent a verification link to ${email}: verify your email, then sign in.`;
  }
  return "Your account has been created, but we couldn't send the verification email. Sign in to get a new link.";
};

/** What the /VerifyEmail page shows. */
export const VERIFY_STATE = {
  /** The link has no token. */
  MISSING: "missing",
  /** Waiting for the press: opening the page changes nothing (mail scanners open every link). */
  READY: "ready",
  DONE: "done",
  /** The server refused the link: used, expired or not ours. */
  FAILED: "failed",
};

/**
 * The page's heading and sentence.
 *
 * @param {string} state One of VERIFY_STATE.
 * @param {string} [serverMessage] Why the server refused the link, for FAILED.
 * @returns {{title: string, text: string}}
 */
export const verifyPageText = (state, serverMessage) => {
  switch (state) {
    case VERIFY_STATE.MISSING:
      return {
        title: "This link is not complete",
        text: "Open the link from the verification email again, or ask for a new one.",
      };
    case VERIFY_STATE.DONE:
      return { title: "Your email is verified", text: "Thank you. Your Xcutta account is ready to use." };
    case VERIFY_STATE.FAILED:
      return {
        title: "We couldn't verify your email",
        text: serverMessage || "This link is not valid or has already been used.",
      };
    default:
      return {
        title: "Verify your email address",
        text: "Press the button to confirm that this email address is yours.",
      };
  }
};
